/**
 * useUserManagement Hook
 *
 * Custom hook for managing user CRUD operations.
 * Provides centralized functions for interacting with the User Management API.
 */

import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import { notifications } from '@mantine/notifications';
import type {
  UserModelView,
  CreateUserViewModel,
  UpdateUserViewModel,
  AdminPasswordResetViewModel,
  AssignRolesViewModel,
  UserFilterViewModel,
  ResponseData,
} from '../types';

export function useUserManagement() {
  const httpClient = useHttpClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch users with filters
   */
  const fetchUsers = useCallback(
    async (filters: UserFilterViewModel) => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();

        if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
        if (filters.roles) params.append('roles', filters.roles);
        if (filters.isActive !== undefined) params.append('isActive', String(filters.isActive));
        if (filters.emailConfirmed !== undefined) params.append('emailConfirmed', String(filters.emailConfirmed));
        if (filters.isLockedOut !== undefined) params.append('isLockedOut', String(filters.isLockedOut));
        if (filters.createdFrom) params.append('createdFrom', filters.createdFrom);
        if (filters.createdTo) params.append('createdTo', filters.createdTo);
        if (filters.page) params.append('page', String(filters.page));
        if (filters.pageSize) params.append('pageSize', String(filters.pageSize));

        const url = `${getApiUrl('users', '/api/admin/users')}?${params.toString()}`;
        const response = await httpClient.get<ResponseData<UserModelView[]>>(url);

        return {
          users: response.data || [],
          total: response.data?.data?.length ?? 0,
        };
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to fetch users';
        setError(errorMessage);
        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red',
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [httpClient]
  );

  /**
   * Create a new user
   */
  const createUser = useCallback(
    async (data: CreateUserViewModel) => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl('users', '/api/admin/users');
        const response = await httpClient.post<ResponseData<UserModelView>>(url, data);

        notifications.show({
          title: 'Success',
          message: 'User created successfully',
          color: 'green',
        });

        return response.data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to create user';
        setError(errorMessage);
        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red',
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [httpClient]
  );

  /**
   * Get user by ID
   */
  const fetchUser = useCallback(
    async (userId: string) => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl('users', `/api/admin/users/${userId}`);
        const response = await httpClient.get<ResponseData<UserModelView>>(url);
        return response.data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to fetch user';
        setError(errorMessage);
        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red',
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [httpClient]
  );

  /**
   * Update user
   */
  const updateUser = useCallback(
    async (userId: string, data: UpdateUserViewModel) => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl('users', `/api/admin/users/${userId}`);
        const response = await httpClient.put<ResponseData<UserModelView>>(url, data);

        notifications.show({
          title: 'Success',
          message: 'User updated successfully',
          color: 'green',
        });

        return response.data;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to update user';
        setError(errorMessage);
        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red',
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [httpClient]
  );

  /**
   * Delete user
   */
  const deleteUser = useCallback(
    async (userId: string) => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl('users', `/api/admin/users/${userId}`);
        await httpClient.delete<ResponseData<boolean>>(url);

        notifications.show({
          title: 'Success',
          message: 'User deleted successfully',
          color: 'green',
        });
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to delete user';
        setError(errorMessage);
        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red',
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [httpClient]
  );

  /**
   * Reset user password (Admin)
   */
  const resetPassword = useCallback(
    async (userId: string, data: AdminPasswordResetViewModel) => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl('users', `/api/admin/users/${userId}/reset-password`);
        await httpClient.post<ResponseData<boolean>>(url, data);

        notifications.show({
          title: 'Success',
          message: data.sendNotification
            ? 'Password reset successfully and notification sent'
            : 'Password reset successfully',
          color: 'green',
        });
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to reset password';
        setError(errorMessage);
        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red',
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [httpClient]
  );

  /**
   * Assign roles to user
   */
  const assignRoles = useCallback(
    async (userId: string, roles: AssignRolesViewModel) => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl('users', `/api/admin/users/${userId}/roles`);
        await httpClient.post<ResponseData<boolean>>(url, roles);

        notifications.show({
          title: 'Success',
          message: 'Roles assigned successfully',
          color: 'green',
        });
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to assign roles';
        setError(errorMessage);
        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red',
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [httpClient]
  );

  /**
   * Get user roles
   */
  const fetchUserRoles = useCallback(
    async (userId: string) => {
      setLoading(true);
      setError(null);

      try {
        const url = getApiUrl('users', `/api/admin/users/${userId}/roles`);
        const response = await httpClient.get<ResponseData<string[]>>(url);
        return response.data || [];
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to fetch user roles';
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [httpClient]
  );

  return {
    loading,
    error,
    fetchUsers,
    createUser,
    fetchUser,
    updateUser,
    deleteUser,
    resetPassword,
    assignRoles,
    fetchUserRoles,
  };
}
